import React, { useEffect, useState } from 'react';
// import axios from 'axios';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import styles from '../../../styles/DocumentManagement.module.css';
import { UserDetails } from '../../../types/User';
import { useUserDetailsApi } from '../../../hooks/useUserDetails';

interface AgentsListProps {
  agencyId: string;
  displayMode?: 'list' | 'dropdown';
}

const AgentsList: React.FC<AgentsListProps> = ({ agencyId, displayMode = 'list' }) => {
  const [agents, setAgents] = useState<UserDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const { selectedUser, setSelectedUser } = useDocumentContext();
  const { fetchAgencyUsers } = useUserDetailsApi();
  
  useEffect(() => {
    if (!agencyId) return;
    setLoading(true);
    fetchAgencyUsers(agencyId)
      .then((data: any) => {
        setAgents(data || []);
      })
      .finally(() => setLoading(false));
  }, [agencyId, fetchAgencyUsers]);

  const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const agentId = event.target.value;
    if (!agentId) {
      setSelectedUser(null);
      return;
    }
    const agent = agents.find(a => a.id === agentId);
    if (agent) {
      // Map Agent to UserDetails shape for context
      setSelectedUser({
        id: agent.id,
        fullNames: agent.fullNames ?? null,
        lastName: agent.lastName ?? null,
        fullName: agent.fullName,
        displayName: agent.displayName,
        email: agent.email,
        identificationType: agent.identificationType ?? null,
        idNumber: agent.idNumber ?? null,
        dateOfBirth: agent.dateOfBirth ?? null,
        gender: agent.gender ?? null,
        mobileNumber: agent.mobileNumber ?? null,
        // Physical address fields
        unitNumber: agent.unitNumber,
        complex: agent.complex,
        streetNumber: agent.streetNumber,
        streetName: agent.streetName,
        suburb: agent.suburb,
        city: agent.city,
        postalCode: agent.postalCode,
        fullPhysicalAddress: agent.fullPhysicalAddress,
        // Postal address fields (new structured names, fall back to legacy when available)
        postalAddressSameAsResidential: agent.postalAddressSameAsResidential,
        postalUnitNumber: agent.postalUnitNumber ?? null,
        postalComplex: agent.postalComplex ?? null,
        postalStreetNumber: agent.postalStreetNumber ?? null,
        postalStreetName: agent.postalStreetName ?? null,
        postalSuburb: agent.postalSuburb ?? null,
        postalCity: agent.postalCity ?? null,
        postalPostalCode: agent.postalPostalCode ?? null,
        accountStatus: agent.accountStatus ?? null,
        profileComplete: agent.profileComplete ?? false,
        emailVerified: agent.emailVerified ?? false,
        agency: {
          agencyId: agent.agency?.agencyId || '',
          companyName: agent.agency?.companyName || '',
          tradingAsName: agent.agency?.tradingAsName ?? null,
          companyRegistrationNumber: agent.agency?.companyRegistrationNumber ?? null,
          numberOfSeats: agent.agency?.numberOfSeats ?? undefined,
          packagePrice: agent.agency?.packagePrice ?? undefined,
          paymentStatus: agent.agency?.paymentStatus ?? undefined,
          paidUntil: agent.agency?.paidUntil ?? null,
          isPaymentValid: agent.agency?.isPaymentValid ?? undefined,
          isOnTrial: agent.agency?.isOnTrial ?? undefined,
          trialEndDate: agent.agency?.trialEndDate ?? null,
          trialDaysRemaining: agent.agency?.trialDaysRemaining ?? undefined,
          unitNumber: agent.agency?.unitNumber ?? '',
          complexName: agent.agency?.complexName ?? '',
          streetNumber: agent.agency?.streetNumber ?? '',
          suburbName: agent.agency?.suburbName ?? '',
          city: agent.agency?.city ?? '',
          postalCode: agent.agency?.postalCode ?? '',
        },
        role: agent.role,
        roleName: agent.roleName ?? null,
        permissions: agent.permissions ?? [],
        accessLevel: agent.accessLevel ?? 1,
        archived: agent.archived || false,
      });
    }
  };

  if (loading) return <div className={styles.skeleton} />;
  if (!agents.length) return <div>No agents found for this agency.</div>;

  // if (displayMode === 'dropdown') {
    return (
      <div className={styles.agentsContainer}>
        <label htmlFor="agent-select" className={styles.label}>
          Select Agent
        </label>
        <select
          id="agent-select"
          value={selectedUser?.id}
          onChange={handleDropdownChange}
          className={styles.select}
        >
          <option key="default" value="">Choose an agent...</option>
          {agents.map(agent => (
            <option key={agent.id} value={agent.id}>
              {agent.displayName || agent.fullName} ({agent.email}){agent.roleName ? ` - ${agent.roleName}` : ''} {agent.archived ? '(Archived)' : ''}
            </option>
          ))}
        </select>
      </div>
    );
};

export default AgentsList;
